'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/local-api/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRealtime } from '@/contexts/RealtimeContext';
import {
  BellIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  BookmarkIcon,
  CheckIcon,
  XMarkIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  MegaphoneIcon,
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
  UserPlusIcon,
  UserGroupIcon,
  BoltIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  TrashIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { BellIcon as BellSolid } from '@heroicons/react/24/solid';
import Icon from '@/components/ui/AppIcon';


interface Notification {
  id: string;
  recipient_id: string;
  actor_id: string | null;
  post_id: string | null;
  notification_type: 'like' | 'comment' | 'bookmark' | 'project_warning' | 'admin_alert' | 'group_post';
  is_read: boolean;
  created_at: string;
  metadata?: { title?: string; message?: string; project_name?: string; community_id?: string; community_name?: string; author_name?: string; preview?: string } | null;
  actor?: { full_name: string } | null;
  post?: { content: string } | null;
}

interface PendingFriendRequest {
  id: string;
  sender_id: string;
  created_at: string;
  sender_profile: { full_name: string; username?: string; role?: string } | null;
}

interface PendingFollowRequest {
  id: string;
  follower_id: string;
  created_at: string;
  follower_profile: { full_name: string; username?: string; role?: string } | null;
}

interface ActivityEvent {
  id: string;
  type: 'connection_request_received' | 'connection_request_accepted' | 'new_mutual_connection';
  actorName: string;
  created_at: string;
  isNew?: boolean;
}

interface NotificationPreference {
  type: string;
  enabled: boolean;
}

interface DirectMessage {
  id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
  sender_profile: { full_name: string; username?: string } | null;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const typeConfig = {
  like: {
    icon: HeartIcon,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    border: 'border-pink-500/20',
    label: 'liked your post',
    prefLabel: 'Post Likes',
    prefDesc: 'When someone likes your post',
  },
  comment: {
    icon: ChatBubbleLeftIcon,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/20',
    label: 'commented on your post',
    prefLabel: 'Post Comments',
    prefDesc: 'When someone comments on your post',
  },
  bookmark: {
    icon: BookmarkIcon,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    label: 'saved your post',
    prefLabel: 'Saved Item Updates',
    prefDesc: 'When someone saves your post',
  },
  project_warning: {
    icon: ExclamationTriangleIcon,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/20',
    label: 'Project warning',
    prefLabel: 'Project Warnings',
    prefDesc: 'Warnings about projects matching your interests',
  },
  admin_alert: {
    icon: MegaphoneIcon,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    border: 'border-red-500/20',
    label: 'Admin alert',
    prefLabel: 'Admin Alerts',
    prefDesc: 'Important platform announcements from admins',
  },
  group_post: {
    icon: UserGroupIcon,
    color: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/20',
    label: 'posted in your group',
    prefLabel: 'Group Post Alerts',
    prefDesc: 'When new posts appear in your joined groups',
  },
};

const activityConfig = {
  connection_request_received: {
    icon: UserPlusIcon,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    label: 'sent you a connection request',
  },
  connection_request_accepted: {
    icon: CheckIcon,
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    label: 'accepted your connection request',
  },
  new_mutual_connection: {
    icon: UserGroupIcon,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    label: 'is now connected with you',
  },
};

const ALL_TYPES = ['like', 'comment', 'bookmark', 'project_warning', 'admin_alert', 'group_post'] as const;

function getInitials(name: string) {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}

const gradients = [
  'from-blue-500 to-purple-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-red-500',
  'from-pink-500 to-rose-600',
  'from-violet-500 to-indigo-600',
  'from-amber-500 to-orange-600',
];
function getGradient(id: string) {
  return gradients[id.charCodeAt(0) % gradients.length];
}

// Group notifications by date bucket
function groupByDate(notifications: Notification[]): { label: string; items: Notification[] }[] {
  const now = Date.now();
  const groups: Record<string, Notification[]> = { Today: [], 'This Week': [], Earlier: [] };
  notifications.forEach((n) => {
    const diff = now - new Date(n.created_at).getTime();
    const hours = diff / 3600000;
    if (hours < 24) groups['Today'].push(n);
    else if (hours < 168) groups['This Week'].push(n);
    else groups['Earlier'].push(n);
  });
  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

export default function NotificationHub() {
  const { user } = useAuth();
  const router = useRouter();
  const { unreadNotifCount: globalUnreadNotifCount, unreadDmCount: globalUnreadDmCount, markNotifsRead } = useRealtime();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'list' | 'requests' | 'activity' | 'prefs'>('list');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [prefs, setPrefs] = useState<Record<string, boolean>>({});
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [activityFeed, setActivityFeed] = useState<ActivityEvent[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [newActivityCount, setNewActivityCount] = useState(0);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  // Pending requests state
  const [pendingFriendRequests, setPendingFriendRequests] = useState<PendingFriendRequest[]>([]);
  const [pendingFollowRequests, setPendingFollowRequests] = useState<PendingFollowRequest[]>([]);
  const [recentMessages, setRecentMessages] = useState<DirectMessage[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const fetchNotificationsRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const fetchActivityRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const fetchRequestsRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const activityChannelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  // Unique suffix per component instance — prevents StrictMode double-mount reuse
  const instanceIdRef = useRef<string>(Math.random().toString(36).slice(2));

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const pendingRequestsCount = pendingFriendRequests.length + pendingFollowRequests.length;
  const unreadMessagesCount = recentMessages.filter((m) => !m.is_read).length;
  // Use global realtime counts for the badge so it stays in sync across all screens
  const totalBadge = globalUnreadNotifCount + globalUnreadDmCount + pendingRequestsCount;

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('notifications')
      .select(
        '*, actor:user_profiles!notifications_actor_id_fkey(full_name), post:community_posts!notifications_post_id_fkey(content)'
      )
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false })
      .limit(40);
    if (data) setNotifications(data as Notification[]);
    setLoading(false);
  }, [user]);

  const fetchPendingRequests = useCallback(async () => {
    if (!user) return;
    setRequestsLoading(true);
    const supabase = createClient();

    // Fetch pending friend/connection requests (two-step: get rows, then profiles)
    const { data: friendReqRows } = await supabase
      .from('connection_requests')
      .select('id, sender_id, created_at')
      .eq('receiver_id', user.id)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (friendReqRows && friendReqRows.length > 0) {
      const senderIds = friendReqRows.map((r: any) => r.sender_id);
      const { data: senderProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, role')
        .in('id', senderIds);
      const profileMap: Record<string, any> = {};
      (senderProfiles ?? []).forEach((p: any) => { profileMap[p.id] = p; });
      const enriched = friendReqRows.map((r: any) => ({
        ...r,
        sender_profile: profileMap[r.sender_id] ?? null,
      }));
      setPendingFriendRequests(enriched as PendingFriendRequest[]);
    } else {
      setPendingFriendRequests([]);
    }

    // Fetch recent followers (two-step)
    const { data: followRows } = await supabase
      .from('follows')
      .select('id, follower_id, created_at')
      .eq('following_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    if (followRows && followRows.length > 0) {
      const followerIds = followRows.map((r: any) => r.follower_id);
      const { data: followerProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, username, role')
        .in('id', followerIds);
      const profileMap: Record<string, any> = {};
      (followerProfiles ?? []).forEach((p: any) => { profileMap[p.id] = p; });
      const enriched = followRows.map((r: any) => ({
        ...r,
        follower_profile: profileMap[r.follower_id] ?? null,
      }));
      setPendingFollowRequests(enriched as PendingFollowRequest[]);
    } else {
      setPendingFollowRequests([]);
    }

    // Fetch recent direct messages received (two-step)
    const { data: msgRows } = await supabase
      .from('direct_messages')
      .select('id, sender_id, content, is_read, created_at')
      .eq('receiver_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10);

    if (msgRows && msgRows.length > 0) {
      const senderIds = msgRows.map((r: any) => r.sender_id);
      const { data: senderProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, username')
        .in('id', senderIds);
      const profileMap: Record<string, any> = {};
      (senderProfiles ?? []).forEach((p: any) => { profileMap[p.id] = p; });
      const enriched = msgRows.map((r: any) => ({
        ...r,
        sender_profile: profileMap[r.sender_id] ?? null,
      }));
      setRecentMessages(enriched as DirectMessage[]);
    } else {
      setRecentMessages([]);
    }

    setRequestsLoading(false);
  }, [user]);

  const fetchActivity = useCallback(async () => {
    if (!user) return;
    setActivityLoading(true);
    const supabase = createClient();
    const events: ActivityEvent[] = [];

    // Incoming pending + recently accepted requests where I am receiver (two-step)
    const { data: receivedRows } = await supabase
      .from('connection_requests')
      .select('id, status, created_at, sender_id')
      .eq('receiver_id', user.id)
      .in('status', ['pending', 'accepted'])
      .order('created_at', { ascending: false })
      .limit(20);

    if (receivedRows && receivedRows.length > 0) {
      const senderIds = receivedRows.map((r: any) => r.sender_id);
      const { data: senderProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name')
        .in('id', senderIds);
      const profileMap: Record<string, string> = {};
      (senderProfiles ?? []).forEach((p: any) => { profileMap[p.id] = p.full_name; });

      receivedRows.forEach((r: any) => {
        const name = profileMap[r.sender_id] || 'Someone';
        if (r.status === 'pending') {
          events.push({
            id: `req-${r.id}`,
            type: 'connection_request_received',
            actorName: name,
            created_at: r.created_at,
          });
        } else if (r.status === 'accepted') {
          events.push({
            id: `mutual-${r.id}`,
            type: 'new_mutual_connection',
            actorName: name,
            created_at: r.created_at,
          });
        }
      });
    }

    // Requests I sent that got accepted (two-step)
    const { data: sentAcceptedRows } = await supabase
      .from('connection_requests')
      .select('id, created_at, receiver_id')
      .eq('sender_id', user.id)
      .eq('status', 'accepted')
      .order('created_at', { ascending: false })
      .limit(20);

    if (sentAcceptedRows && sentAcceptedRows.length > 0) {
      const receiverIds = sentAcceptedRows.map((r: any) => r.receiver_id);
      const { data: receiverProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name')
        .in('id', receiverIds);
      const profileMap: Record<string, string> = {};
      (receiverProfiles ?? []).forEach((p: any) => { profileMap[p.id] = p.full_name; });

      sentAcceptedRows.forEach((r: any) => {
        const name = profileMap[r.receiver_id] || 'Someone';
        events.push({
          id: `accepted-${r.id}`,
          type: 'connection_request_accepted',
          actorName: name,
          created_at: r.created_at,
        });
      });
    }

    // Sort all events by date desc
    events.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    setActivityFeed(events.slice(0, 30));
    setActivityLoading(false);
  }, [user]);

  // Keep refs in sync
  useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications;
  }, [fetchNotifications]);

  useEffect(() => {
    fetchActivityRef.current = fetchActivity;
  }, [fetchActivity]);

  useEffect(() => {
    fetchRequestsRef.current = fetchPendingRequests;
  }, [fetchPendingRequests]);

  const fetchPrefs = useCallback(async () => {
    if (!user) return;
    setPrefsLoading(true);
    const supabase = createClient();
    const { data } = await supabase
      .from('notification_preferences')
      .select('type, enabled')
      .eq('user_id', user.id);

    const map: Record<string, boolean> = {};
    // Default all to true
    ALL_TYPES.forEach((t) => (map[t] = true));
    if (data) {
      (data as NotificationPreference[]).forEach((p) => {
        map[p.type] = p.enabled;
      });
    }
    setPrefs(map);
    setPrefsLoading(false);
  }, [user]);

  // Fetch on open
  useEffect(() => {
    if (open && user) {
      fetchNotifications();
      fetchPrefs();
      fetchActivity();
      fetchPendingRequests();
      // Signal to RealtimeContext that notifications have been viewed
      markNotifsRead();
    }
  }, [open, user, fetchNotifications, fetchPrefs, fetchActivity, fetchPendingRequests, markNotifsRead]);

  // Reset new activity count when activity tab is viewed
  useEffect(() => {
    if (view === 'activity') {
      setNewActivityCount(0);
    }
  }, [view]);

  // Real-time subscription for notifications
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    // Include instance ID so each mount gets a brand-new channel name
    const channelName = `notifications_realtime_${user.id}_${instanceIdRef.current}`;

    // Remove any existing channel before creating a new one
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          fetchNotificationsRef.current?.();
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
      // Rotate instance ID so the next mount also gets a fresh name
      instanceIdRef.current = Math.random().toString(36).slice(2);
    };
  }, [user?.id]);

  // Real-time subscription for connection activity
  useEffect(() => {
    if (!user) return;
    const supabase = createClient();
    const activityChannelName = `activity_realtime_${user.id}_${instanceIdRef.current}`;

    // Remove any existing channel before creating a new one
    if (activityChannelRef.current) {
      supabase.removeChannel(activityChannelRef.current);
      activityChannelRef.current = null;
    }

    const channel = supabase
      .channel(activityChannelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'connection_requests',
          filter: `receiver_id=eq.${user.id}`,
        },
        () => {
          fetchActivityRef.current?.();
          fetchRequestsRef.current?.();
          setNewActivityCount((c) => c + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'connection_requests',
        },
        (payload) => {
          // Accepted request — relevant if I was sender or receiver
          if (payload.new?.status === 'accepted') {
            fetchActivityRef.current?.();
            fetchRequestsRef.current?.();
            setNewActivityCount((c) => c + 1);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'follows',
          filter: `following_id=eq.${user.id}`,
        },
        () => {
          fetchRequestsRef.current?.();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'direct_messages',
          filter: `receiver_id=eq.${user.id}`,
        },
        () => {
          fetchRequestsRef.current?.();
        }
      )
      .subscribe();

    activityChannelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      activityChannelRef.current = null;
    };
  }, [user?.id]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markAllRead = async () => {
    if (!user) return;
    const supabase = createClient();
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('recipient_id', user.id)
      .eq('is_read', false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const toggleRead = async (id: string, currentlyRead: boolean) => {
    const supabase = createClient();
    await supabase
      .from('notifications')
      .update({ is_read: !currentlyRead })
      .eq('id', id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: !currentlyRead } : n))
    );
  };

  const dismissNotification = async (id: string) => {
    setDismissingId(id);
    const supabase = createClient();
    await supabase.from('notifications').delete().eq('id', id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setDismissingId(null);
  };

  const handleNotificationClick = async (n: Notification) => {
    if (!n.is_read) {
      const supabase = createClient();
      await supabase.from('notifications').update({ is_read: true }).eq('id', n.id);
      setNotifications((prev) =>
        prev.map((item) => (item.id === n.id ? { ...item, is_read: true } : item))
      );
    }
    setOpen(false);
    if (n.notification_type === 'group_post' && n.metadata?.community_id) {
      router.push(`/community-groups/${n.metadata.community_id}`);
    } else if (n.post_id) {
      router.push('/community-network');
    } else if (n.notification_type === 'project_warning') {
      router.push('/project-reviews');
    }
  };

  const togglePref = async (type: string) => {
    if (!user) return;
    const newVal = !prefs[type];
    setPrefs((prev) => ({ ...prev, [type]: newVal }));
    const supabase = createClient();
    await supabase.from('notification_preferences').upsert(
      { user_id: user.id, type, enabled: newVal, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,type' }
    );
  };

  // Accept friend request
  const handleAcceptFriendRequest = async (requestId: string) => {
    if (!user) return;
    setProcessingRequest(requestId);
    const supabase = createClient();
    const { error } = await supabase
      .from('connection_requests')
      .update({ status: 'accepted' })
      .eq('id', requestId);
    if (!error) {
      setPendingFriendRequests((prev) => prev.filter((r) => r.id !== requestId));
      fetchActivityRef.current?.();
    }
    setProcessingRequest(null);
  };

  // Reject friend request
  const handleRejectFriendRequest = async (requestId: string) => {
    if (!user) return;
    setProcessingRequest(requestId);
    const supabase = createClient();
    const { error } = await supabase
      .from('connection_requests')
      .update({ status: 'rejected' })
      .eq('id', requestId);
    if (!error) {
      setPendingFriendRequests((prev) => prev.filter((r) => r.id !== requestId));
    }
    setProcessingRequest(null);
  };

  // Remove follower (optional — just removes from list visually for now)
  const handleRemoveFollower = async (followId: string) => {
    setProcessingRequest(followId);
    const supabase = createClient();
    await supabase.from('follows').delete().eq('id', followId);
    setPendingFollowRequests((prev) => prev.filter((r) => r.id !== followId));
    setProcessingRequest(null);
  };

  // Filter notifications by user prefs (hide disabled types from list)
  const visibleNotifications = notifications.filter(
    (n) => prefs[n.notification_type] !== false
  );
  const groupedNotifications = groupByDate(visibleNotifications);

  if (!user) return null;

  // Calculate right offset to keep panel within viewport on mobile
  const getPanelStyle = (): React.CSSProperties => {
    if (!panelRef.current) return { maxHeight: '560px', maxWidth: 'calc(100vw - 0.5rem)', right: 0 };
    const rect = panelRef.current.getBoundingClientRect();
    const panelWidth = Math.min(360, window.innerWidth - 8);
    // right edge of bell button relative to viewport
    const rightEdge = window.innerWidth - rect.right;
    // left edge of panel if anchored right-0
    const panelLeft = rect.right - panelWidth;
    // if panel would overflow left, shift it right
    const overflow = panelLeft < 4 ? 4 - panelLeft : 0;
    return {
      maxHeight: '560px',
      width: `${panelWidth}px`,
      right: -(overflow),
    };
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => {
          setOpen((v) => !v);
          setView('list');
        }}
        className="relative p-2 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Notifications"
      >
        {totalBadge > 0 ? (
          <BellSolid style={{ width: 20, height: 20 }} className="text-primary" />
        ) : (
          <BellIcon style={{ width: 20, height: 20 }} />
        )}
        {totalBadge > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full bg-negative text-white text-[9px] font-bold flex items-center justify-center px-0.5">
            {totalBadge > 9 ? '9+' : totalBadge}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="absolute top-full mt-2 bg-card border border-border rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col" style={getPanelStyle()}>
          
          {/* ── HEADER ── */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card flex-shrink-0">
            <div className="flex items-center gap-2">
              {view === 'prefs' ? (
                <button
                  onClick={() => setView('list')}
                  className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors mr-0.5"
                >
                  <ArrowLeftIcon style={{ width: 14, height: 14 }} />
                </button>
              ) : (
                <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center">
                  <BellSolid style={{ width: 13, height: 13 }} className="text-primary" />
                </div>
              )}
              <span className="text-sm font-semibold text-foreground">
                {view === 'prefs' ? 'Preferences' : 'Notifications'}
              </span>
              {view === 'list' && unreadCount > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary leading-none">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              {view === 'list' && unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  title="Mark all as read"
                  className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <CheckCircleIcon style={{ width: 15, height: 15 }} />
                </button>
              )}
              {view === 'list' && (
                <button
                  onClick={() => setView('prefs')}
                  title="Notification preferences"
                  className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Cog6ToothIcon style={{ width: 15, height: 15 }} />
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <XMarkIcon style={{ width: 15, height: 15 }} />
              </button>
            </div>
          </div>

          {/* ── TAB BAR ── */}
          {view !== 'prefs' && (
            <div className="flex border-b border-border bg-card flex-shrink-0">
              {[
                { key: 'list', label: 'Alerts', badge: unreadCount > 0 ? unreadCount : null, badgeColor: 'bg-primary/15 text-primary' },
                { key: 'requests', label: 'Requests', icon: UserPlusIcon, badge: (pendingRequestsCount + unreadMessagesCount) > 0 ? pendingRequestsCount + unreadMessagesCount : null, badgeColor: 'bg-blue-500/15 text-blue-400' },
                { key: 'activity', label: 'Activity', icon: BoltIcon, badge: newActivityCount > 0 ? newActivityCount : null, badgeColor: 'bg-blue-500/15 text-blue-400' },
              ].map(({ key, label, icon: Icon, badge, badgeColor }) => (
                <button
                  key={key}
                  onClick={() => setView(key as any)}
                  className={`flex-1 py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 relative ${
                    view === key
                      ? 'text-primary' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {Icon && <Icon style={{ width: 11, height: 11 }} />}
                  {label}
                  {badge !== null && (
                    <span className={`text-[9px] font-bold px-1 py-0.5 rounded-full leading-none ${badgeColor}`}>
                      {badge}
                    </span>
                  )}
                  {view === key && (
                    <span className="absolute bottom-0 left-1/4 right-1/4 h-0.5 rounded-full bg-primary" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* ── PREFERENCES VIEW ── */}
          {view === 'prefs' && (
            <div className="overflow-y-auto flex-1">
              {prefsLoading ? (
                <div className="flex justify-center items-center py-10">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="p-3 space-y-1">
                  <p className="text-[11px] text-muted-foreground px-1 mb-3">
                    Choose which notifications you want to receive.
                  </p>
                  {ALL_TYPES.map((type) => {
                    const cfg = typeConfig[type];
                    const TypeIcon = cfg.icon;
                    const enabled = prefs[type] !== false;
                    return (
                      <div
                        key={type}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-muted/50 transition-colors"
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                          <TypeIcon style={{ width: 14, height: 14 }} className={cfg.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground">{cfg.prefLabel}</p>
                          <p className="text-[11px] text-muted-foreground leading-snug">{cfg.prefDesc}</p>
                        </div>
                        <button
                          onClick={() => togglePref(type)}
                          className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                            enabled ? 'bg-primary' : 'bg-muted-foreground/30'
                          }`}
                          aria-label={`Toggle ${cfg.prefLabel}`}
                        >
                          <span
                            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                              enabled ? 'translate-x-4' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── REQUESTS VIEW ── */}
          {view === 'requests' && (
            <div className="overflow-y-auto flex-1">
              {requestsLoading ? (
                <div className="flex justify-center items-center py-10">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : pendingFriendRequests.length === 0 && pendingFollowRequests.length === 0 && recentMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 gap-2">
                  <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-1">
                    <UserPlusIcon style={{ width: 22, height: 22 }} className="text-muted-foreground/50" />
                  </div>
                  <p className="text-sm font-medium text-foreground">No pending requests</p>
                  <p className="text-xs text-muted-foreground text-center px-8 leading-relaxed">
                    Friend requests, followers, and messages will appear here
                  </p>
                </div>
              ) : (
                <div>
                  {/* Friend Requests Section */}
                  {pendingFriendRequests.length > 0 && (
                    <div>
                      <div className="px-4 pt-3 pb-1.5">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                          <UserPlusIcon style={{ width: 10, height: 10 }} />
                          Friend Requests
                          <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-400">{pendingFriendRequests.length}</span>
                        </p>
                      </div>
                      <div className="divide-y divide-border/50">
                        {pendingFriendRequests.map((req) => {
                          const name = req.sender_profile?.full_name || 'Someone';
                          const isProcessing = processingRequest === req.id;
                          return (
                            <div key={req.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                              <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${getGradient(req.sender_id)} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}>
                                {getInitials(name)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-foreground truncate">{name}</p>
                                {req.sender_profile?.username && (
                                  <p className="text-[10px] text-muted-foreground">@{req.sender_profile.username}</p>
                                )}
                                <p className="text-[10px] text-muted-foreground/60 mt-0.5">{timeAgo(req.created_at)}</p>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <button
                                  onClick={() => handleAcceptFriendRequest(req.id)}
                                  disabled={isProcessing}
                                  title="Accept"
                                  className="h-7 px-2.5 rounded-lg bg-green-500/15 text-green-400 hover:bg-green-500 hover:text-white transition-colors flex items-center gap-1 text-[11px] font-medium disabled:opacity-50"
                                >
                                  {isProcessing ? (
                                    <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <><CheckIcon style={{ width: 11, height: 11 }} /> Accept</>
                                  )}
                                </button>
                                <button
                                  onClick={() => handleRejectFriendRequest(req.id)}
                                  disabled={isProcessing}
                                  title="Decline"
                                  className="h-7 w-7 rounded-lg bg-muted text-muted-foreground hover:bg-red-500/15 hover:text-red-400 transition-colors flex items-center justify-center disabled:opacity-50"
                                >
                                  <XMarkIcon style={{ width: 13, height: 13 }} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* New Followers Section */}
                  {pendingFollowRequests.length > 0 && (
                    <div className={pendingFriendRequests.length > 0 ? 'border-t border-border' : ''}>
                      <div className="px-4 pt-3 pb-1.5">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                          <UserGroupIcon style={{ width: 10, height: 10 }} />
                          New Followers
                          <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-violet-500/15 text-violet-400">{pendingFollowRequests.length}</span>
                        </p>
                      </div>
                      <div className="divide-y divide-border/50">
                        {pendingFollowRequests.map((req) => {
                          const name = req.follower_profile?.full_name || 'Someone';
                          const isProcessing = processingRequest === req.id;
                          return (
                            <div key={req.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                              <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${getGradient(req.follower_id)} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}>
                                {getInitials(name)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-foreground truncate">{name}</p>
                                {req.follower_profile?.username && (
                                  <p className="text-[10px] text-muted-foreground">@{req.follower_profile.username}</p>
                                )}
                                <p className="text-[10px] text-violet-400 font-medium mt-0.5">Started following you · {timeAgo(req.created_at)}</p>
                              </div>
                              <button
                                onClick={() => handleRemoveFollower(req.id)}
                                disabled={isProcessing}
                                title="Remove follower"
                                className="h-7 w-7 rounded-lg bg-muted text-muted-foreground hover:bg-red-500/15 hover:text-red-400 transition-colors flex items-center justify-center disabled:opacity-50 flex-shrink-0"
                              >
                                {isProcessing ? (
                                  <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <XMarkIcon style={{ width: 13, height: 13 }} />
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Messages Section */}
                  {recentMessages.length > 0 && (
                    <div className={(pendingFriendRequests.length > 0 || pendingFollowRequests.length > 0) ? 'border-t border-border' : ''}>
                      <div className="px-4 pt-3 pb-1.5">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                          <ChatBubbleOvalLeftEllipsisIcon style={{ width: 10, height: 10 }} />
                          Messages
                          {unreadMessagesCount > 0 && (
                            <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">{unreadMessagesCount} unread</span>
                          )}
                        </p>
                      </div>
                      <div className="divide-y divide-border/50">
                        {recentMessages.map((msg) => {
                          const name = msg.sender_profile?.full_name || 'Someone';
                          const snippet = msg.content.length > 55 ? msg.content.slice(0, 55) + '…' : msg.content;
                          return (
                            <button
                              key={msg.id}
                              onClick={() => { setOpen(false); router.push('/community-network'); }}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left ${!msg.is_read ? 'bg-primary/5' : ''}`}
                            >
                              <div className="relative flex-shrink-0">
                                <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${getGradient(msg.sender_id)} flex items-center justify-center text-white font-bold text-xs`}>
                                  {getInitials(name)}
                                </div>
                                {!msg.is_read && (
                                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary border-2 border-card" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-xs font-semibold text-foreground truncate">{name}</p>
                                  <p className="text-[10px] text-muted-foreground/60 flex-shrink-0">{timeAgo(msg.created_at)}</p>
                                </div>
                                <p className="text-[11px] text-muted-foreground truncate mt-0.5">{snippet}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="border-t border-border px-4 py-2.5 flex items-center justify-end flex-shrink-0">
                <button
                  onClick={() => { setOpen(false); router.push('/community-network'); }}
                  className="text-xs text-primary font-medium hover:underline"
                >
                  Open Messages →
                </button>
              </div>
            </div>
          )}

          {/* ── ACTIVITY FEED VIEW ── */}
          {view === 'activity' && (
            <div className="overflow-y-auto flex-1 flex flex-col">
              <div className="flex-1">
                {activityLoading ? (
                  <div className="flex justify-center items-center py-10">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : activityFeed.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-2">
                    <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-1">
                      <BoltIcon style={{ width: 22, height: 22 }} className="text-muted-foreground/50" />
                    </div>
                    <p className="text-sm font-medium text-foreground">No activity yet</p>
                    <p className="text-xs text-muted-foreground text-center px-8 leading-relaxed">
                      Connection requests, acceptances, and mutual connections will appear here
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/50">
                    {activityFeed.map((event) => {
                      const cfg = activityConfig[event.type];
                      const EventIcon = cfg.icon;
                      return (
                        <button
                          key={event.id}
                          onClick={() => { setOpen(false); router.push('/community-network'); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors text-left"
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                            <EventIcon style={{ width: 14, height: 14 }} className={cfg.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-foreground leading-snug">
                              <span className="font-semibold">{event.actorName}</span>{' '}
                              <span className="text-muted-foreground">{cfg.label}</span>
                            </p>
                            <p className="text-[10px] text-muted-foreground/60 mt-0.5">{timeAgo(event.created_at)}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="border-t border-border px-4 py-2.5 flex items-center justify-end flex-shrink-0">
                <button
                  onClick={() => { setOpen(false); router.push('/community-network'); }}
                  className="text-xs text-primary font-medium hover:underline"
                >
                  Go to Community →
                </button>
              </div>
            </div>
          )}

          {/* ── NOTIFICATIONS LIST VIEW ── */}
          {view === 'list' && (
            <div className="overflow-y-auto flex-1 flex flex-col">
              <div className="flex-1">
                {loading ? (
                  <div className="flex justify-center items-center py-10">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : visibleNotifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-2">
                    <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-1">
                      <BellIcon style={{ width: 22, height: 22 }} className="text-muted-foreground/50" />
                    </div>
                    <p className="text-sm font-medium text-foreground">All caught up!</p>
                    <p className="text-xs text-muted-foreground text-center px-8 leading-relaxed">
                      You'll be notified about comments, saves, project warnings, and admin alerts
                    </p>
                  </div>
                ) : (
                  <div>
                    {groupedNotifications.map(({ label, items }) => (
                      <div key={label}>
                        {/* Date group header */}
                        <div className="px-4 pt-3 pb-1.5 sticky top-0 bg-card z-10">
                          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">{label}</p>
                        </div>
                        <div className="divide-y divide-border/50">
                          {items.map((n) => {
                            const cfg = typeConfig[n.notification_type] ?? typeConfig.comment;
                            const TypeIcon = cfg.icon;
                            const actorName = n.actor?.full_name || 'Someone';

                            let mainText: React.ReactNode;
                            let subText: string | null = null;

                            if (n.notification_type === 'admin_alert') {
                              mainText = (
                                <span className="font-semibold text-red-400">{n.metadata?.title || 'Admin Alert'}</span>
                              );
                              subText = n.metadata?.message || null;
                            } else if (n.notification_type === 'project_warning') {
                              mainText = (
                                <>
                                  <span className="font-semibold text-orange-400">Project Warning</span>
                                  {n.metadata?.project_name && (
                                    <span className="text-muted-foreground"> — {n.metadata.project_name}</span>
                                  )}
                                </>
                              );
                              subText = n.metadata?.message || null;
                            } else if (n.notification_type === 'group_post') {
                              mainText = (
                                <>
                                  <span className="font-semibold">{n.metadata?.author_name || n.actor?.full_name || 'A member'}</span>{' '}
                                  <span className="text-muted-foreground">posted in </span>
                                  <span className="font-semibold text-violet-400">{n.metadata?.community_name || 'your group'}</span>
                                </>
                              );
                              subText = n.metadata?.preview ? `"${n.metadata.preview.length > 60 ? n.metadata.preview.slice(0, 60) + '…' : n.metadata.preview}"` : null;
                            } else {
                              const postSnippet = n.post?.content
                                ? n.post.content.length > 50 ? n.post.content.slice(0, 50) + '…' : n.post.content
                                : null;
                              mainText = (
                                <>
                                  <span className="font-semibold">{actorName}</span>{' '}
                                  <span className="text-muted-foreground">{cfg.label}</span>
                                </>
                              );
                              subText = postSnippet ? `"${postSnippet}"` : null;
                            }

                            return (
                              <div
                                key={n.id}
                                className={`flex items-start gap-3 px-4 py-3 transition-colors group relative ${
                                  !n.is_read ? 'bg-primary/[0.04]' : 'hover:bg-muted/30'
                                } ${dismissingId === n.id ? 'opacity-50 pointer-events-none' : ''}`}
                              >
                                {/* Unread indicator bar */}
                                {!n.is_read && (
                                  <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-primary" />
                                )}

                                {/* Clickable content */}
                                <button
                                  onClick={() => handleNotificationClick(n)}
                                  className="flex items-start gap-3 flex-1 text-left min-w-0"
                                >
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                                    <TypeIcon style={{ width: 14, height: 14 }} className={cfg.color} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-foreground leading-snug">{mainText}</p>
                                    {subText && (
                                      <p className="text-[11px] text-muted-foreground mt-0.5 truncate italic">{subText}</p>
                                    )}
                                    <p className="text-[10px] text-muted-foreground/50 mt-1">{timeAgo(n.created_at)}</p>
                                  </div>
                                </button>

                                {/* Action buttons — visible on hover */}
                                <div className="flex items-center gap-0.5 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); toggleRead(n.id, n.is_read); }}
                                    title={n.is_read ? 'Mark as unread' : 'Mark as read'}
                                    className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    {n.is_read ? (
                                      <EyeSlashIcon style={{ width: 12, height: 12 }} />
                                    ) : (
                                      <EyeIcon style={{ width: 12, height: 12 }} />
                                    )}
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); dismissNotification(n.id); }}
                                    title="Dismiss"
                                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                                  >
                                    <TrashIcon style={{ width: 12, height: 12 }} />
                                  </button>
                                </div>

                                {/* Unread dot (always visible when unread) */}
                                {!n.is_read && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2 group-hover:opacity-0 transition-opacity" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              {visibleNotifications.length > 0 && (
                <div className="border-t border-border px-4 py-2.5 flex items-center justify-between flex-shrink-0">
                  <button
                    onClick={() => setView('prefs')}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  >
                    <Cog6ToothIcon style={{ width: 12, height: 12 }} />
                    Preferences
                  </button>
                  <button
                    onClick={() => { setOpen(false); router.push('/notifications'); }}
                    className="text-xs text-primary font-medium hover:underline"
                  >
                    View all →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
